/**
 * Maps local API paths (/api/{scope}/{action}) to backend sub-URLs.
 * The base URL is resolved dynamically:
 *   brancy.ir  →  https://api.brancy.ir/
 *   brancy.app →  https://api.brancy.app/  (default)
 * Used by clientFetchApi to call the backend directly (bypassing Next.js API proxy)
 * for all routes EXCEPT /api/user/*.
 *
 * These paths are the NORMALIZED output of toLocalApiPath() — always lowercase scope, lowerFirst action.
 */
export const API_ROUTE_MAP: Record<string, string> = {
  // ── account ───────────────────────────────────────────
  "/api/account/createPartner": "Instagramer/Account/CreatePartner",
  "/api/account/getInfo": "Instagramer/Account/GetInfo",
  "/api/account/getPartners": "Instagramer/Account/GetPartners",
  "/api/account/getTitleInfo": "User/Account/GetTitleInfo",
  "/api/account/removePartner": "Instagramer/Account/RemovePartner",
  "/api/account/updatePartner": "Instagramer/Account/UpdatePartner",

  // ── address ───────────────────────────────────────────
  "/api/address/createAddressByPostalCode": "User/Address/CreateAddressByPostalCode",
  "/api/address/getAddresses": "User/Address/GetAddresses",
  "/api/address/getAddressInputType": "User/Address/GetAddressInputType",
  "/api/address/updateUserAddress": "User/Address/UpdateUserAddress",

  // ── ai ────────────────────────────────────────────────
  "/api/ai/createPrompt": "Instagramer/AI/CreatePrompt",
  "/api/ai/getPrompt": "Instagramer/AI/GetPrompt",
  "/api/ai/getPromptAnalysis": "Instagramer/AI/GetPromptAnalysis",
  "/api/ai/hasPageAnalysis": "Instagramer/AI/HasPageAnalysis",
  "/api/ai/getPrompts": "Instagramer/AI/GetPrompts",
  "/api/ai/getRelatedPrompts": "Instagramer/AI/GetRelatedPrompts",
  "/api/ai/getTools": "Instagramer/AI/GetTools",
  "/api/ai/sendTestMessage": "Instagramer/AI/SendTestMessage",
  "/api/ai/getAllAiModels": "Instagramer/AI/GetAllAiModels",
  "/api/ai/updateModel": "Instagramer/AI/UpdateModel",
  "/api/ai/getAiModels": "Instagramer/AI/GetAiModels",

  // ── mediaai ───────────────────────────────────────────
  "/api/mediaai/getImages": "Instagramer/MediaAi/GetImages",
  "/api/mediaai/getVideos": "Instagramer/MediaAi/GetVideos",
  "/api/mediaai/getImageCreators": "Instagramer/MediaAi/GetImageCreators",
  "/api/mediaai/getVideoCreators": "Instagramer/MediaAi/GetVideoCreators",
  "/api/mediaai/getImagePrompts": "Instagramer/MediaAi/GetImagePrompts",
  "/api/mediaai/getImagePromptCategories": "Instagramer/MediaAi/GetImagePromptCategories",
  "/api/mediaai/getImageUsage": "Instagramer/MediaAi/GetImageUsage",
  "/api/mediaai/getVideoUsage": "Instagramer/MediaAi/GetVideoUsage",
  "/api/mediaai/createImage": "Instagramer/MediaAi/CreateImage",
  "/api/mediaai/createVideo": "Instagramer/MediaAi/CreateVideo",

  // ── business ─────────────────────────────────────────
  "/api/business/get": "User/Business/Get",
  "/api/business/getshort": "User/Business/GetShort",
  "/api/business/search": "User/Business/Search",
  "/api/business/getExplorer": "User/Business/GetExplorer",
  "/api/business/getFavorites": "User/Business/GetFavorites",

  // ── autoacceptfollower ────────────────────────────────
  "/api/autoacceptfollower/checkAvailability": "Instagramer/AutoAcceptFollower/CheckAvailability",
  "/api/autoacceptfollower/getAcceptedFollowers": "Instagramer/AutoAcceptFollower/GetAcceptedFollowers",
  "/api/autoacceptfollower/getCondition": "Instagramer/AutoAcceptFollower/GetCondition",
  "/api/autoacceptfollower/getFigure": "Instagramer/AutoAcceptFollower/GetFigure",
  "/api/autoacceptfollower/updateCondition": "Instagramer/AutoAcceptFollower/UpdateCondition",

  // ── authorize ─────────────────────────────────────────
  "/api/authorize/authorizeInstagramerByCardNumber": "Business/Authorize/AuthorizeInstagramerByCardNumber",
  "/api/authorize/createAdvertiser": "Business/Authorize/CreateAdvertiser",
  "/api/authorize/createShopper": "Business/Authorize/CreateShopper",
  "/api/authorize/getAuthorizeLevel": "Business/Authorize/GetAuthorizeLevel",
  "/api/authorize/getAuthorizeUserType": "Business/Authorize/GetAuthorizeUserType",
  "/api/authorize/getInstagramerAuthorizeType": "Business/Authorize/GetInstagramerAuthorizeType",
  "/api/authorize/getShopLogestics": "Business/Authorize/GetShopLogestics",
  "/api/authorize/saveSupportLogestic": "Business/Authorize/SaveSupportLogestic",
  "/api/authorize/setShopAddress": "Business/Authorize/SetShopAddress",
  "/api/authorize/userAuthorizeByNationalCard": "Business/Authorize/UserAuthorizeByNationalCard",

  // ── bio ───────────────────────────────────────────────
  "/api/bio/getAnnouncement": "Instagramer/bio/GetAnnouncement",
  "/api/bio/getChannels": "Instagramer/Bio/GetChannels",
  "/api/bio/getContact": "Instagramer/bio/GetContact",
  "/api/bio/getCustomBanners": "Instagramer/Bio/GetCustomBanners",
  "/api/bio/getCustomDomain": "Instagramer/Bio/GetCustomDomain",
  "/api/bio/getCustomProfile": "Instagramer/Bio/GetCustomProfile",
  "/api/bio/getFaqs": "Instagramer/bio/GetFaqs",
  "/api/bio/getMyLink": "Instagramer/Bio/GetMyLink",
  "/api/bio/getOrderItems": "Instagramer/Bio/GetOrderItems",
  "/api/bio/getTotalInsight": "Instagramer/Bio/GetTotalInsight",
  "/api/bio/getTotalInsightFigures": "Instagramer/Bio/GetTotalInsightFigures",
  "/api/bio/getVideoInsight": "Instagramer/Bio/GetVideoInsight",
  "/api/bio/getWorkingHours": "Instagramer/Bio/GetWorkingHours",
  "/api/bio/saveAparatPage": "Instagramer/Bio/SaveAparatPage",
  "/api/bio/saveYoutubePage": "Instagramer/Bio/SaveYoutubePage",
  "/api/bio/saveTwitchPage": "Instagramer/Bio/SaveTwitchPage",
  "/api/bio/toggleCustomDomain": "Instagramer/Bio/ToggleCustomDomain",
  "/api/bio/connectCustomDomain": "Instagramer/Bio/ConnectCustomDomain",
  "/api/bio/deleteCustomDomain": "Instagramer/Bio/DeleteCustomDomain",
  "/api/bio/verifyCustomDomainDns": "Instagramer/Bio/VerifyCustomDomainDns",
  "/api/bio/toggleFeatureBox": "Instagramer/Bio/ToggleFeatureBox",
  "/api/bio/updateAnnouncement": "Instagramer/bio/UpdateAnnouncement",
  "/api/bio/updateContact": "Instagramer/bio/UpdateContact",
  "/api/bio/updateCustomBanners": "Instagramer/Bio/UpdateCustomBanners",
  "/api/bio/updateCustomDomain": "Instagramer/Bio/UpdateCustomDomain",
  "/api/bio/updateCustomProfile": "Instagramer/Bio/UpdateCustomProfile",
  "/api/bio/updateFaqs": "Instagramer/bio/UpdateFaqs",
  "/api/bio/updateOrderItems": "Instagramer/Bio/UpdateOrderItems",
  "/api/bio/updateWorkingHours": "Instagramer/Bio/UpdateWorkingHours",
  "/api/bio/getTermsAndCondtions": "Instagramer/Bio/GetTermsAndCondtions",
  "/api/bio/updateTermsAndConditions": "Instagramer/Bio/UpdateTermsAndConditions",

  // ── comment ───────────────────────────────────────────
  "/api/comment/createExportLiveComment": "Instagramer/Comment/CreateExportLiveComment",
  "/api/comment/generateCommentBaseAI": "Instagramer/Comment/GenerateCommentBaseAI",
  "/api/comment/getExportLiveComments": "Instagramer/Comment/GetExportLiveComments",
  "/api/comment/getInbox": "Instagramer/Comment/GetInbox",
  "/api/comment/getMediaComments": "Instagramer/Comment/GetMediaComments",
  "/api/comment/pauseLiveAutoReply": "Instagramer/Comment/PauseLiveAutoReply",
  "/api/comment/resumeLiveAutoReply": "Instagramer/Comment/ResumeLiveAutoReply",
  "/api/comment/updateLiveAutoReply": "Instagramer/Comment/UpdateLiveAutoReply",

  // ── dayevent ──────────────────────────────────────────
  "/api/dayevent/getEvents": "Instagramer/DayEvent/GetEvents",
  "/api/dayevent/getEventIdeas": "Instagramer/DayEvent/GetEventIdeas",
  "/api/dayevent/createEventIdea": "Instagramer/DayEvent/CreateEventIdea",
  "/api/dayevent/getCustomEventIdeas": "Instagramer/DayEvent/GetCustomEventIdeas",

  // ── flow ──────────────────────────────────────────────
  "/api/flow/createMasterFlow": "Instagramer/Flow/CreateMasterFlow",
  "/api/flow/getMasterFlow": "Instagramer/Flow/GetMasterFlow",
  "/api/flow/getMasterFlows": "Instagramer/Flow/GetMasterFlows",
  "/api/flow/getRelatedMasterFlows": "Instagramer/Flow/GetRelatedMasterFlows",
  "/api/flow/getExportPhoneNumbers": "Instagramer/Flow/GetExportPhoneNumbers",
  "/api/flow/getPhoneNumbers": "Instagramer/Flow/GetPhoneNumbers",
  "/api/flow/getShortMasterFlow": "Instagramer/Flow/GetShortMasterFlow",

  // ── feature ──────────────────────────────────────────
  "api/feature/hasFeature": "Instagramer/Feature/HasFeature",
  "api/feature/hasFeatureCount": "Instagramer/Feature/HasFeatureCount",

  // ── hashtag ───────────────────────────────────────────
  "/api/hashtag/analysisPageHashtags": "Instagramer/hashtag/AnalysisPageHashtags",
  "/api/hashtag/createHashtagList": "Instagramer/hashtag/createHashtagList",
  "/api/hashtag/deleteHashtagList": "Instagramer/hashtag/DeleteHashtagList",
  "/api/hashtag/getHashtagList": "Instagramer/hashtag/GetHashtagList",
  "/api/hashtag/getHashtagsByImage": "Instagramer/hashtag/GetHashtagsByImage",
  "/api/hashtag/getTrendHashtag": "Instagramer/hashtag/GetTrendHashtag",
  "/api/hashtag/hidePageAnalysisHashtag": "Instagramer/hashtag/HidePageAnalysisHashtag",
  "/api/hashtag/hidePictureAnalysisHashtag": "Instagramer/hashtag/HidePictureAnalysisHashtag",
  "/api/hashtag/searchHashtag": "Instagramer/hashtag/searchHashtag",
  "/api/hashtag/updateHashtagList": "Instagramer/hashtag/UpdateHashtagList",

  // ── home ──────────────────────────────────────────────
  "/api/home/getLastMessages": "Instagramer/Home/GetLastMessages",
  "/api/home/getPageSummary": "Instagramer/Home/GetPageSummary",
  "/api/home/getTiles": "Instagramer/Home/GetTiles",

  // ── instagramer (direct search endpoints) ─────────────
  "/api/instagramer/searchLocations": "Instagramer/searchLocations",
  "/api/instagramer/searchPeople": "Instagramer/searchPeople",
  "/api/users/searchPeople": "Instagramer/Users/searchPeople",

  // ── likecomment ───────────────────────────────────────
  "/api/likecomment/checkAvailability": "Instagramer/LikeComment/CheckAvailability",
  "/api/likecomment/getCondition": "Instagramer/LikeComment/GetCondition",
  "/api/likecomment/getFigure": "Instagramer/LikeComment/GetFigure",
  "/api/likecomment/getLikedComments": "Instagramer/LikeComment/GetLikedComments",
  "/api/likecomment/updateCondition": "Instagramer/LikeComment/UpdateCondition",

  // ── likelastpostfollower ──────────────────────────────
  "/api/likelastpostfollower/checkAvailability": "Instagramer/LikeLastPostFollower/CheckAvailability",
  "/api/likelastpostfollower/getCondition": "Instagramer/LikeLastPostFollower/GetCondition",
  "/api/likelastpostfollower/getFigure": "Instagramer/LikeLastPostFollower/GetFigure",
  "/api/likelastpostfollower/getLikedFollowers": "Instagramer/LikeLastPostFollower/GetLikedFollowers",
  "/api/likelastpostfollower/updateCondition": "Instagramer/LikeLastPostFollower/UpdateCondition",

  // ── link ──────────────────────────────────────────────
  "/api/link/createLink": "Instagramer/link/CreateLink",
  "/api/link/deleteLink": "Instagramer/link/deleteLink",
  "/api/link/getAllLinkInsight": "Instagramer/Link/GetAllLinkInsight",
  "/api/link/getAllLinks": "Instagramer/link/GetAllLinks",
  "/api/link/getLinkInsight": "Instagramer/Link/GetLinkInsight",
  "/api/link/updateLink": "Instagramer/link/UpdateLink",
  "/api/link/updateOrders": "Instagramer/link/UpdateOrders",

  // ── lottery ───────────────────────────────────────────
  "/api/lottery/createLottery": "Instagramer/Lottery/CreateLottery",
  "/api/lottery/getFullLottery": "Instagramer/Lottery/GetFullLottery",
  "/api/lottery/getLastBannerSetting": "Instagramer/Lottery/GetLastBannerSetting",
  "/api/lottery/getLastTermsUis": "Instagramer/Lottery/GetLastTermsUis",
  "/api/lottery/getLotteryInfo": "Instagramer/Lottery/GetLotteryInfo",
  "/api/lottery/getShortLotteries": "Instagramer/Lottery/GetShortLotteries",
  "/api/lottery/publishBanner": "Instagramer/Lottery/PublishBanner",
  "/api/lottery/publishTerms": "Instagramer/Lottery/PublishTerms",
  "/api/lottery/rejectLottery": "Instagramer/Lottery/RejectLottery",

  // ── message ───────────────────────────────────────────
  "/api/message/changeCategory": "Instagramer/Message/ChangeCategory",
  "/api/message/createGeneralAutoReply": "Instagramer/Message/CreateGeneralAutoReply",
  "/api/message/getAutoReplySetting": "Instagramer/Message/GetAutoReplySetting",
  "/api/message/getDirectInbox": "Instagramer/Message/GetDirectInbox",
  "/api/message/getDirectParentItem": "Instagramer/Message/GetDirectParentItem",
  "/api/message/getGeneralAutoReplies": "Instagramer/Message/GetGeneralAutoReplies",
  "/api/message/getIceBreaker": "Instagramer/Message/GetIceBreaker",
  "/api/message/getPersistentMenu": "Instagramer/Message/GetPersistentMenu",
  "/api/message/getThread": "Instagramer/Message/GetThread",
  "/api/message/pauseAllAutoReplies": "Instagramer/Message/PauseAllAutoReplies",
  "/api/message/pauseAllGeneralAutoReplies": "Instagramer/Message/PauseAllGeneralAutoReplies",
  "/api/message/pauseGeneralAutoReply": "Instagramer/Message/PauseGeneralAutoReply",
  "/api/message/pinThread": "Instagramer/Message/PinThread",
  "/api/message/resumeAllAutoReplies": "Instagramer/Message/ResumeAllAutoReplies",
  "/api/message/resumeAllGeneralAutoReplies": "Instagramer/Message/ResumeAllGeneralAutoReplies",
  "/api/message/resumeGeneralAutoReply": "Instagramer/Message/ResumeGeneralAutoReply",
  "/api/message/sendReaction": "Instagramer/Message/SendReaction",
  "/api/message/sendUnReaction": "Instagramer/Message/SendUnReaction",
  "/api/message/setDefaultLanguage": "Instagramer/Message/SetDefaultLanguage",
  "/api/message/toggleCheckFollowerTemplate": "Instagramer/Message/ToggleCheckFollowerTemplate",
  "/api/message/toggleHideCommentAutoReply": "Instagramer/Message/ToggleHideCommentAutoReply",
  "/api/message/toggleIceBreaker": "Instagramer/Message/ToggleIceBreaker",
  "/api/message/toggleLikeReplyAutoReply": "Instagramer/Message/ToggleLikeReplyAutoReply",
  "/api/message/togglePersistentMenu": "Instagramer/Message/TogglePersistentMenu",
  "/api/message/updateCheckFollowerTemplate": "Instagramer/Message/UpdateCheckFollowerTemplate",
  "/api/message/updateIceBreaker": "Instagramer/Message/UpdateIceBreaker",
  "/api/message/updatePersistentMenu": "Instagramer/Message/UpdatePersistentMenu",

  // ── order ─────────────────────────────────────────────
  "/api/order/acceptOrder": "Shopper/Order/AcceptOrder",
  "/api/order/createOrder": "User/Order/CreateOrder",
  "/api/order/getFullOrder": "Shopper/Order/GetFullOrder",
  "/api/userorder/getFullOrder": "User/Order/GetFullOrder",
  "/api/order/getOrderPaymentLink": "User/Order/GetOrderPaymentLink",
  "/api/order/getOrdersByStatus": "Shopper/Order/GetOrdersByStatus",
  "/api/userorder/getOrdersByStatus": "User/Order/GetOrdersByStatus",
  "/api/order/getOrdersByStatuses": "Shopper/Order/GetOrdersByStatuses",
  "/api/userorder/getOrdersByStatuses": "User/Order/GetOrdersByStatuses",
  "/api/order/getParcelInfo": "Shopper/Order/GetParcelInfo",
  "/api/userorder/getParcelInfo": "User/Order/GetParcelInfo",
  "/api/userorder/rejectorder": "User/Order/RejectOrder",
  "/api/order/getShippingRequestType": "Shopper/Order/GetShippingRequestType",
  "/api/order/readyOrderForShipping": "Shopper/Order/ReadyOrderForShipping",
  "/api/order/rejectOrder": "Shopper/Order/RejectOrder",
  "/api/order/sendOrderByNonRequestType": "Shopper/Order/SendOrderByNonRequestType",
  "/api/order/sentOrderByNonRequestType": "Shopper/Order/SentOrderByNonRequestType",
  "/api/order/sentOrderByParcelId": "Shopper/Order/SentOrderByParcelId",
  "/api/order/setNonTrackingIdOrderDelivered": "Shopper/Order/SetNonTrackingIdOrderDelivered",

  // ── post ──────────────────────────────────────────────
  "/api/post/changeCommentingStatus": "Instagramer/post/ChangeCommentingStatus",
  "/api/post/createExportComments": "Instagramer/Post/CreateExportComments",
  "/api/post/deleteDraft": "Instagramer/Post/deleteDraft",
  "/api/post/deletePrePost": "Instagramer/Post/deletePrePost",
  "/api/post/generateCaptionWithAI": "Instagramer/Post/GenerateCaptionWithAI",
  "/api/post/getBestPublishTime": "Instagramer/Post/GetBestPublishTime",
  "/api/post/getCaptionPromptExamples": "Instagramer/Post/GetCaptionPromptExamples",
  "/api/post/getDraft": "Instagramer/Post/GetDraft",
  "/api/post/getExportComments": "Instagramer/Post/GetExportComments",
  "/api/post/getForceMedias": "Instagramer/Post/GetForceMedias",
  "/api/post/getPostByGuid": "Instagramer/Post/GetPostByGuid",
  "/api/post/getPostByScrollingDown": "Instagramer/Post/GetPostByScrollingDown",
  "/api/post/getPostCards": "Instagramer/Post/GetPostCards",
  "/api/post/getPostInfo": "Instagramer/Post/GetPostInfo",
  "/api/post/getPostInsightInfo": "Instagramer/Post/GetPostInsightInfo",
  "/api/post/getPosts": "Instagramer/Post/GetPosts",
  "/api/post/getPrePost": "Instagramer/Post/GetPrePost",
  "/api/post/getPublishLimitContent": "Instagramer/Post/GetPublishLimitContent",
  "/api/post/getShortPost": "Instagramer/Post/GetShortPost",
  "/api/post/pauseAutoReply": "Instagramer/Post/PauseAutoReply",
  "/api/post/publishCarousel": "Instagramer/Post/PublishCarousel",
  "/api/post/publishImageFeed": "Instagramer/Post/PublishImageFeed",
  "/api/post/publishReels": "Instagramer/Post/PublishReels",
  "/api/post/resumeAutoReply": "Instagramer/Post/ResumeAutoReply",
  "/api/post/updateAutoReply": "Instagramer/Post/UpdateAutoReply",

  // ── preinstagramer ────────────────────────────────────
  "/api/preinstagramer/getFacebookRedirect": "PreInstagramer/GetFacebookRedirect",
  "/api/preinstagramer/getInstagramRedirect": "PreInstagramer/GetInstagramRedirect",
  "/api/preinstagramer/checkUserIsNew": "PreInstagramer/CheckUserIsNew",
  "/api/preinstagramer/sendCode": "PreInstagramer/SendCode",
  "/api/preinstagramer/verifyCode": "PreInstagramer/VerifyCode",

  // ── product ───────────────────────────────────────────
  "api/product/getSuggestedCategory": "shopper/Product/GetSuggestedCategory",
  "/api/product/changeAvailableProduct": "shopper/Product/ChangeAvailableProduct",
  "/api/product/createProductInstance": "shopper/Product/CreateProductInstance",
  "/api/product/createProducts": "shopper/Product/CreateProducts",
  "/api/product/createSubProducts": "shopper/Product/CreateSubProducts",
  "/api/product/getFullProduct": "shopper/Product/GetFullProduct",
  "/api/product/getFullProductList": "shopper/Product/GetFullProductList",
  "/api/product/getLastCategory": "shopper/Product/GetLastCategory",
  "/api/product/getLastProductSpecification": "shopper/Product/GetLastProductSpecification",
  "/api/product/getLastTempIdAndNonProductsCount": "shopper/Product/GetLastTempIdAndNonProductsCount",
  "/api/product/getMainCategoryList": "shopper/Product/GetMainCategoryList",
  "/api/product/getMaxSize": "Shopper/Product/GetMaxSize",
  "/api/product/getMediaSuggestion": "shopper/Product/GetMediaSuggestion",
  "/api/product/getNextProduct": "shopper/Product/GetNextProduct",
  "/api/product/getPreviousProduct": "shopper/Product/GetPreviousProduct",
  "/api/product/getProductByTempId": "shopper/Product/GetProductByTempId",
  "/api/product/getProductCandidates": "shopper/Product/GetProductCandidates",
  "/api/product/getBioProductList": "Shopper/Product/GetBioProductList",
  "/api/product/getProductList": "shopper/Product/GetProductList",
  "/api/product/getSeconderyCategoryList": "shopper/Product/GetSeconderyCategoryList",
  "/api/product/getSuggestedDescription": "Shopper/Product/GetSuggestedDescription",
  "/api/product/getSuggestedPrice": "shopper/Product/GetSuggestedPrice",
  "/api/product/getSuggestedPriceV2": "shopper/Product/GetSuggestedPriceV2",
  "/api/product/getVariations": "shopper/Product/GetVariations",
  "/api/product/insertProductMedia": "shopper/Product/InsertProductMedia",
  "/api/product/saveProductSuggestion": "shopper/Product/SaveProductSuggestion",
  "/api/product/searchProducts": "shopper/Product/SearchProducts",
  "/api/product/unLoadProduct": "shopper/Product/UnLoadProduct",
  "/api/product/updateChildrenMediaStatus": "shopper/Product/UpdateChildrenMediaStatus",
  "/api/product/updateShowInBio": "Shopper/Product/UpdateShowInBio",
  "/api/product/updateSecondaryProductDetails": "shopper/Product/UpdateSecondaryProductDetails",
  "/api/product/updateSelfMediaStatus": "shopper/Product/UpdateSelfMediaStatus",
  "/api/product/updateSpecificationOrder": "shopper/Product/UpdateSpecificationOrder",

  // ── psg ───────────────────────────────────────────────
  "/api/psg/getPackageFeatureDetails": "Instagramer/PSG/GetPackageFeatureDetails",
  "/api/psg/getPackagePrices": "Instagramer/PSG/GetPackagePrices",
  "/api/psg/getPackageRedirectUrl": "Instagramer/PSG/GetPackageRedirectUrl",
  "/api/psg/getRedirectReserveFeaturePrice": "Instagramer/PSG/GetRedirectReserveFeaturePrice",
  "/api/psg/getReserveFeaturePrices": "Instagramer/PSG/GetReserveFeaturePrices",

  // ── session ───────────────────────────────────────────
  "/api/session/approvePartnerRequest": "user/Session/ApprovePartnerRequest",
  "/api/session/deleteSession": "User/Session/DeleteSession",
  "/api/session/getPartners": "user/Session/GetPartners",
  "/api/session/getSessions": "User/Session/GetSessions",
  "/api/session/rejectPartnerRequest": "user/Session/RejectPartnerRequest",

  // ── shop ──────────────────────────────────────────────
  "/api/shop/addCard": "user/shop/AddCard",
  "/api/shop/getAllCard": "user/shop/GetAllCard",
  "/api/shop/getfilters": "user/shop/getfilters",
  "/api/shop/getfullproduct": "user/shop/getfullproduct",
  "/api/shop/getfullshop": "user/shop/getfullshop",
  "/api/shop/getInstagramerCard": "user/shop/GetInstagramerCard",
  "/api/shop/getLogesticPrice": "user/shop/GetLogesticPrice",
  "/api/shop/getPriceHistory": "user/shop/GetPriceHistory",
  "/api/shop/getProductComments": "user/shop/GetProductComments",
  "/api/shop/getProductCoupon": "user/shop/GetProductCoupon",
  "/api/shop/getproducthashtags": "user/shop/getproducthashtags",
  "/api/shop/getshopproducts": "user/shop/getshopproducts",
  "/api/shop/reportProduct": "user/shop/ReportProduct",
  "/api/shop/searchshop": "user/shop/searchshop",
  "/api/shop/searchshopproducts": "user/shop/searchshopproducts",
  "/api/shop/updateFavoriteProduct": "user/shop/UpdateFavoriteProduct",

  // ── coupon ───────────────────────────────────────────
  "/api/coupon/getCoupons": "Shopper/Coupon/GetCoupons",
  "/api/coupon/createCoupon": "Shopper/Coupon/CreateCoupon",
  "/api/coupon/updateCoupon": "Shopper/Coupon/UpdateCoupon",
  "/api/coupon/activateCoupon": "Shopper/Coupon/ActivateCoupon",
  "/api/coupon/deleteCoupon": "Shopper/Coupon/DeleteCoupon",

  // ── statistics ────────────────────────────────────────
  "/api/statistics/getBestFollowers": "Instagramer/Statistics/GetBestFollowers",
  "/api/statistics/getDemographicInsight": "Instagramer/Statistics/GetDemographicInsight",
  "/api/statistics/getEngagmentStatistics": "Instagramer/Statistics/GetEngagmentStatistics",
  "/api/statistics/getFollowerStatistics": "Instagramer/Statistics/GetFollowerStatistics",
  "/api/statistics/getOverview": "Instagramer/Statistics/GetOverview",
  "/api/statistics/getTimeAnalysis": "Instagramer/Statistics/GetTimeAnalysis",

  // ── story ─────────────────────────────────────────────
  "/api/story/createExportReplies": "Instagramer/Story/CreateExportReplies",
  "/api/story/deleteDraft": "Instagramer/Story/deleteDraft",
  "/api/story/deletePreStory": "Instagramer/Story/DeletePreStory",
  "/api/story/getDraft": "Instagramer/Story/GetDraft",
  "/api/story/getExportReplies": "Instagramer/Story/GetExportReplies",
  "/api/story/getLastActiveStories": "Instagramer/Story/GetLastActiveStories",
  "/api/story/getNextStories": "Instagramer/Story/GetNextStories",
  "/api/story/getPreStory": "Instagramer/Story/GetPreStory",
  "/api/story/getStory": "Instagramer/Story/GetStory",
  "/api/story/getStoryInsight": "Instagramer/Story/GetStoryInsight",
  "/api/story/getStoryReplies": "Instagramer/Story/GetStoryReplies",
  "/api/story/loadingStoryPage": "Instagramer/Story/LoadingStoryPage",
  "/api/story/pauseAutoReply": "Instagramer/Story/PauseAutoReply",
  "/api/story/publishImage": "Instagramer/Story/PublishImage",
  "/api/story/publishVideo": "Instagramer/Story/PublishVideo",
  "/api/story/resumeAutoReply": "Instagramer/Story/ResumeAutoReply",
  "/api/story/searchViewers": "Instagramer/Story/SearchViewers",
  "/api/story/updateAutoReply": "Instagramer/Story/UpdateAutoReply",

  // ── systemticket ──────────────────────────────────────
  "/api/systemticket/addSystemTicketItem": "User/SystemTicket/AddSystemTicketItem",
  "/api/systemticket/createSystemTicketBaseOrderId": "User/SystemTicket/CreateSystemTicketBaseOrderId",
  "/api/systemticket/getSystemInbox": "User/SystemTicket/GetSystemInbox",
  "/api/systemticket/getSystemTicket": "User/SystemTicket/GetSystemTicket",
  "/api/systemticket/reportToAdmin": "User/SystemTicket/ReportToAdmin",
  "/api/systemticket/seenSystemTicket": "User/SystemTicket/SeenSystemTicket",
  "/api/systemticket/updateSystemTicketHideStatus": "User/SystemTicket/UpdateSystemTicketHideStatus",
  "/api/systemticket/updateSystemTicketPinStatus": "User/SystemTicket/UpdateSystemTicketPinStatus",

  // ── ticket ────────────────────────────────────────────
  "/api/ticket/addSystemTicketItem": "Instagramer/Ticket/AddSystemTicketItem",
  "/api/ticket/closeFbTicket": "Instagramer/Ticket/CloseFbTicket",
  "/api/ticket/closePlatformTicket": "Instagramer/Ticket/ClosePlatformTicket",
  "/api/ticket/createPlatformTicket": "Instagramer/Ticket/CreatePlatformTicket",
  "/api/ticket/getFbInbox": "Instagramer/Ticket/GetFbInbox",
  "/api/ticket/getFbTicket": "Instagramer/Ticket/GetFbTicket",
  "/api/ticket/getFbTicketItems": "Instagramer/Ticket/GetFbTicketItems",
  "/api/ticket/getPlatformTicket": "Instagramer/Ticket/GetPlatformTicket",
  "/api/ticket/getPlatformTicketInbox": "Instagramer/Ticket/GetPlatformTicketInbox",
  "/api/ticket/getPlatformTicketInsight": "Instagramer/Ticket/GetPlatformTicketInsight",
  "/api/ticket/getSystemInbox": "Instagramer/Ticket/GetSystemInbox",
  "/api/ticket/getSystemTicket": "Instagramer/Ticket/GetSystemTicket",
  "/api/ticket/readFbTicket": "Instagramer/Ticket/ReadFbTicket",
  "/api/ticket/seenPlatformTicket": "Instagramer/Ticket/SeenPlatformTicket",
  "/api/ticket/seenSystemTicket": "Instagramer/Ticket/SeenSystemTicket",
  "/api/ticket/updateFbTicketHiddenStatus": "Instagramer/Ticket/UpdateFbTicketHiddenStatus",
  "/api/ticket/updateFbTicketItems": "Instagramer/Ticket/UpdateFbTicketItems",
  "/api/ticket/updatePlatformTicket": "Instagramer/Ticket/UpdatePlatformTicket",
  "/api/ticket/updatePlatformTicketPinStatus": "Instagramer/Ticket/UpdatePlatformTicketPinStatus",
  "/api/ticket/updateSystemTicketHideStatus": "Instagramer/Ticket/UpdateSystemTicketHideStatus",
  "/api/ticket/updateSystemTicketPinStatus": "Instagramer/Ticket/UpdateSystemTicketPinStatus",

  // ── uisetting ─────────────────────────────────────────
  "/api/uisetting/get": "Instagramer/UiSetting/Get",
  "/api/uisetting/update": "Instagramer/UiSetting/Update",

  // ── unfollowallfollowing ──────────────────────────────
  "/api/unfollowallfollowing/checkAvailability": "Instagramer/UnfollowAllFollowing/CheckAvailability",
  "/api/unfollowallfollowing/getCondition": "Instagramer/UnfollowAllFollowing/GetCondition",
  "/api/unfollowallfollowing/getFigure": "Instagramer/UnfollowAllFollowing/GetFigure",
  "/api/unfollowallfollowing/getUnFollowings": "Instagramer/UnfollowAllFollowing/GetUnFollowings",
  "/api/unfollowallfollowing/updateCondition": "Instagramer/UnfollowAllFollowing/UpdateCondition",

  // ── Wallet ──────────────────────────────
  "/api/wallet/getBallanceHistory": "/Business/Wallet/GetBallanceHistory",
  "/api/wallet/getInstagramerBankCards": "/Business/Wallet/GetInstagramerBankCards",
  "/api/wallet/addCardNumber": "/Business/Wallet/AddCardNumber",
  "/api/wallet/setDefaultCard": "/Business/Wallet/SetDefaultCard",
  "/api/wallet/settleRequest": "/Business/Wallet/SettleRequest",
  "/api/wallet/getGenerallBallance": "/Business/Wallet/GetGeneralBallance",
  "/api/wallet/getSubInvoices": "/Business/Wallet/GetSubInvoices",
  "/api/wallet/getInvoices": "/Business/Wallet/GetInvoices",
};

/**
 * Look up the backend sub-URL for a given local API path.
 * Returns null if the path is not found (caller should fall back to proxy or dynamic catch-all).
 */
export function resolveBackendSubUrl(localPath: string): string | null {
  // Normalize: remove query string
  const clean = localPath.split("?")[0];
  // Try exact match first
  if (API_ROUTE_MAP[clean]) return API_ROUTE_MAP[clean];

  // Try case-insensitive match
  const lower = clean.toLowerCase();
  for (const [key, value] of Object.entries(API_ROUTE_MAP)) {
    if (key.toLowerCase() === lower) return value;
  }

  return null;
}
